import CopyrightIcon from "@mui/icons-material/Copyright";
import { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Box, Typography, Dialog, DialogContent, Grow } from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useAuth } from "../../context/AuthContext";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CircularProgress from "@mui/material/CircularProgress";
import CheckIcon from "@mui/icons-material/Check";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import LaptopIcon from "@mui/icons-material/Laptop";
import DesktopWindowsIcon from "@mui/icons-material/DesktopWindows";
import TabletIcon from "@mui/icons-material/Tablet";
import DevicesIcon from "@mui/icons-material/Devices";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import BusinessIcon from "@mui/icons-material/Business";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import EnhancedEncryptionOutlinedIcon from "@mui/icons-material/EnhancedEncryptionOutlined";
import SpeedOutlinedIcon from "@mui/icons-material/SpeedOutlined";
import { motion, AnimatePresence } from "framer-motion";
import { useReducedMotion } from "./ui/useReducedMotion";
import {
  ThemeScope, Button, Input, Label,
  Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter,
  Alert, Badge, Separator,
} from "./ui/shadcn";

// Design tokens (Design 16 - corporate banking portal, light)
const INK = "#0b1220";
const MUTED = "#475569";
const EASE = [0.22, 1, 0.36, 1];
const ACCENT_GRADIENT = "linear-gradient(90deg, #1d4ed8 0%, #0891b2 55%, #14b8a6 100%)";
const SHAKE_X = [0, -7, 7, -4, 4, 0];

const THEME_VARS = {
  "--background": "#ffffff",
  "--foreground": INK,
  "--card": "#ffffff",
  "--card-foreground": INK,
  "--primary": "#1d4ed8",
  "--primary-foreground": "#ffffff",
  "--secondary": "#eff6ff",
  "--secondary-foreground": "#1e3a8a",
  "--muted": "#f8fafc",
  "--muted-foreground": MUTED,
  "--accent": "#eff6ff",
  "--destructive": "#b91c1c",
  "--destructive-foreground": "#ffffff",
  "--border": "#d5deea",
  "--input": "#7b8aa0",
  "--ring": "#1d4ed8",
  "--radius": "0.75rem",
};

// Static isometric line tile (30 degree grid, no animation)
const ISO_TILE =
  "url(\"data:image/svg+xml," +
  encodeURIComponent(
    "<svg xmlns='http://www.w3.org/2000/svg' width='52' height='30' viewBox='0 0 52 30'><path d='M0 0L52 30M0 30L52 0M0 0V30M26 0V30M52 0V30' fill='none' stroke='#2563eb' stroke-opacity='0.13' stroke-width='1'/></svg>"
  ) +
  "\")";
const PATTERN_MASK = "radial-gradient(ellipse 90% 80% at 50% 40%, #000 25%, rgba(0,0,0,0.45) 100%)";
const GLOWS =
  "radial-gradient(ellipse 50% 40% at 0% 0%, rgba(37,99,235,0.08) 0%, transparent 70%), radial-gradient(ellipse 45% 40% at 100% 100%, rgba(20,184,166,0.09) 0%, transparent 70%)";

const MotionButton = motion.create(Button);
const MotionCard = motion.create(Card);

function LoginDesign16({ connectionString }) {
  const path = connectionString;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { login } = useAuth();
  const reduce = useReducedMotion();

  const autoIdentifier = searchParams.get("identifier");
  const autoUsername = searchParams.get("UserName");
  const autoMobile = searchParams.get("mobile");
  const autoEmail = searchParams.get("email");
  const autoPassword = searchParams.get("password");
  const redirectPathFromQuery = searchParams.get("redirect");

  const [identifier, setIdentifier] = useState("");
  const [userPassword, setUserPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isCompanySelectionVisible, setIsCompanySelectionVisible] = useState(false);

  const [errorMessage, setErrorMessage] = useState("");
  const [isAutoLogin, setIsAutoLogin] = useState(false);
  const [capsLockOn, setCapsLockOn] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  // Session limit picker
  const [sessionPickerData, setSessionPickerData] = useState(null);
  const [kickingSessionId, setKickingSessionId] = useState(null);

  // Device fingerprint (created once, persisted in localStorage)
  const getDeviceId = () => {
    let did = localStorage.getItem("_did");
    if (!did) {
      if (window.crypto && typeof window.crypto.randomUUID === "function") {
        did = window.crypto.randomUUID();
      } else {
        // Fallback for HTTP (non-localhost) — crypto.randomUUID requires HTTPS
        did = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
          const r = (Math.random() * 16) | 0;
          return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
        });
      }
      localStorage.setItem("_did", did);
    }
    return did;
  };

  const getBrowserName = () => {
    const ua = navigator.userAgent;
    if (ua.includes("Chrome") && !ua.includes("Edg")) return `Chrome on ${getOSName()}`;
    if (ua.includes("Firefox")) return `Firefox on ${getOSName()}`;
    if (ua.includes("Safari") && !ua.includes("Chrome")) return `Safari on ${getOSName()}`;
    if (ua.includes("Edg")) return `Edge on ${getOSName()}`;
    return `Browser on ${getOSName()}`;
  };

  const getOSName = () => {
    const ua = navigator.userAgent;
    if (ua.includes("Windows")) return "Windows";
    if (ua.includes("Mac")) return "macOS";
    if (ua.includes("Linux")) return "Linux";
    return "Unknown OS";
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins  = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days  = Math.floor(diff / 86400000);
    if (mins < 1)   return "just now";
    if (mins < 60)  return `${mins}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  const deviceIcon = (type) => {
    if (type === "mobile")  return <PhoneAndroidIcon sx={{ fontSize: 20, color: "#1d4ed8" }} />;
    if (type === "tablet")  return <TabletIcon sx={{ fontSize: 20, color: "#1d4ed8" }} />;
    if (type === "desktop") return <DesktopWindowsIcon sx={{ fontSize: 20, color: "#1d4ed8" }} />;
    return <LaptopIcon sx={{ fontSize: 20, color: "#1d4ed8" }} />;
  };

  const handleTogglePasswordVisibility = () => setShowPassword((prev) => !prev);

  const isEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s?.trim());

  useEffect(() => {
    const handleMessage = (ev) => {
      try {
        const data = typeof ev.data === "string" ? JSON.parse(ev.data) : ev.data;
        if (!data || !data.type) return;
        if (data.type === "BIOMETRIC_LOGIN") {
          FN_HandleLogin(data.identifier, data.password);
        }
        if (data.type === "BIOMETRIC_FAILED") {
          setErrorMessage("Biometric authentication failed or cancelled.");
        }
        if (data.type === "BIOMETRIC_NOT_AVAILABLE") {
          setErrorMessage("Biometrics not available on this device.");
        }
      } catch (err) {
        console.warn("Error parsing message from native:", err);
      }
    };
    window.addEventListener("message", handleMessage);
    document.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
      document.removeEventListener("message", handleMessage);
    };
  }, []);

  const isLikelyMobile = (s) => {
    if (!s) return false;
    const cleaned = s.replace(/[\s\-\(\)\+]/g, "");
    const digits = cleaned.replace(/\D/g, "");
    const nonDigitCount = cleaned.replace(/\d/g, "").length;
    return (digits.length >= 6 && digits.length <= 15 && nonDigitCount === 0);
  };

  const determineIdentifierType = (id) => {
    const trimmed = id?.trim() || "";
    if (isEmail(trimmed)) return "email";
    if (isLikelyMobile(trimmed)) return "mobile";
    return "UserName";
  };

  useEffect(() => {
    const ai = autoIdentifier || autoUsername || autoMobile || autoEmail;
    if (ai && autoPassword) {
      setIsAutoLogin(true);
      setIdentifier(ai);
      setUserPassword(autoPassword);
      FN_HandleLogin(ai, autoPassword);
    }
  }, [autoIdentifier, autoUsername, autoMobile, autoEmail, autoPassword]);

  const FN_HandleLogin = async (overrideIdentifier, overridePassword, kickSessionId = null) => {
    setErrorMessage("");
    setIsLoading(true);
    const id = (overrideIdentifier ?? identifier ?? "").trim();
    const pwd = overridePassword ?? userPassword;

    if (!id || !pwd) {
      setErrorMessage("Please provide identifier and password.");
      setIsLoading(false);
      return;
    }

    try {
      const identifierType = determineIdentifierType(id);

      // Build device info
      let deviceInfo = {
        deviceId:   getDeviceId(),
        deviceName: getBrowserName(),
        platform:   navigator.platform,
        deviceType: "web",
      };
      if (window.electronAPI) {
        try { deviceInfo = await window.electronAPI.getDeviceInfo(); } catch (_) {}
      }

      const payload = {
        identifier: id,
        password: pwd,
        type: identifierType,
        deviceId:     deviceInfo.deviceId,
        deviceName:   deviceInfo.deviceName,
        platform:     deviceInfo.platform,
        deviceType:   deviceInfo.deviceType,
        kickSessionId: kickSessionId,
      };
      const response = await axios.post(`${path}/api/login`, payload,{ withCredentials: true });

      // Session limit reached — show device picker
      if (response.data.sessionLimitReached) {
        setSessionPickerData(response.data);
        setIsLoading(false);
        return;
      }

      if (!response.data.success) {
        setErrorMessage(response.data.message || "Invalid credentials.");
        return;
      }

      // Store session token for all future API requests
      if (response.data.sessionToken) {
        sessionStorage.setItem("sessionToken", response.data.sessionToken);
        axios.defaults.headers.common["x-session-token"] = response.data.sessionToken;
      }

      if (!overrideIdentifier && window.ReactNativeWebView?.postMessage) {
        window.ReactNativeWebView.postMessage(
          JSON.stringify({ type: "STORE_CREDENTIALS", identifier: id, password: pwd })
        );
      }

      const user = response.data.user;
      const companyRes = await axios.get(`${path}/api/companies?companyId=${user.CompId}`);

      if (!companyRes.data.success || !companyRes.data.companies?.length) {
        navigate("/registerCompany", { replace: true, state: { pendingUser: user, password: pwd } });
        return;
      }

      const currentLoginCompanyList = companyRes.data.companies;
      const actualSelectedCompany =
        currentLoginCompanyList.find((c) => c.companyId === user.companyId || c.companyId === user.CompId) ||
        currentLoginCompanyList[0];

      const allRes = await axios.get(`${path}/api/All-companies`, { params: { userId: user.UserId } });
      const allCompanies =
        allRes.data.success && allRes.data.data?.length ? allRes.data.data : currentLoginCompanyList;
      const serverIsSuperAdmin = Boolean(allRes.data.isSuperAdmin);

      await login(user, allCompanies, actualSelectedCompany, serverIsSuperAdmin);

      setCompanies(currentLoginCompanyList);
      setSelectedCompany(actualSelectedCompany);

      if (isAutoLogin || currentLoginCompanyList.length === 1) {
        FN_SelectCompany(actualSelectedCompany);
      } else {
        setIsCompanySelectionVisible(true);
      }
    } catch (error) {
      setErrorMessage(error.response?.data?.message || "Login failed.");
      console.error("Login error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const FN_SelectCompany = (companyOverride = null) => {
    const company = companyOverride || selectedCompany;
    if (!company) {
      setErrorMessage("Please select a company.");
      return;
    }
    localStorage.setItem("selectedCompanyId", company.CompId);
    const redirectPath = redirectPathFromQuery || "/main/home";
    navigate(redirectPath);
  };

  const handleCapsLockCheck = (e) => {
    if (e.getModifierState) {
      setCapsLockOn(e.getModifierState("CapsLock"));
    }
  };

  // ── Visual helpers (styling / motion only) ─────────────────────────────────
  const dist = reduce ? 0 : 14;
  // Fade + rise (decorative content only)
  const enter = (delay = 0) => ({
    initial: { opacity: 1, y: dist },
    animate: { opacity: 1, y: 0 },
    transition: { duration: reduce ? 0.15 : 0.5, delay: reduce ? 0 : delay, ease: EASE },
  });
  // Rise only, NO opacity (interactive content: inputs, buttons, company list)
  const rise = (delay = 0) => ({
    initial: { y: dist },
    animate: { y: 0 },
    transition: { duration: reduce ? 0.15 : 0.5, delay: reduce ? 0 : delay, ease: EASE },
  });

  const handleCompanyKeyDown = (e, idx) => {
    const key = e.key;
    if (key === " " || key === "Enter") {
      e.preventDefault();
      setSelectedCompany(companies[idx]);
      return;
    }
    let next = null;
    if (key === "ArrowDown" || key === "ArrowRight") next = (idx + 1) % companies.length;
    if (key === "ArrowUp" || key === "ArrowLeft") next = (idx - 1 + companies.length) % companies.length;
    if (next === null) return;
    e.preventDefault();
    setSelectedCompany(companies[next]);
    const el = document.getElementById("ld16-company-" + next);
    if (el) el.focus();
  };

  const trust = [
    { Icon: VerifiedUserOutlinedIcon, top: "ISO", sub: "Certified", color: "#1d4ed8" },
    { Icon: ReceiptLongOutlinedIcon, top: "GST", sub: "Compliant", color: "#0369a1" },
    { Icon: EnhancedEncryptionOutlinedIcon, top: "256-bit", sub: "Encryption", color: "#0f766e" },
    { Icon: SpeedOutlinedIcon, top: "99.9%", sub: "Uptime", color: "#0d9488" },
  ];

  const capsHelper = (
    <div className="ld16-helper" role="status">
      <AnimatePresence initial={false}>
        {capsLockOn && (
          <motion.span
            key="caps"
            className="ld16-caps"
            initial={{ opacity: 1, y: reduce ? 0 : -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: reduce ? 0.1 : 0.18 }}
          >
            <WarningAmberIcon sx={{ fontSize: 14, color: "#b45309" }} />Caps Lock is ON
          </motion.span>
        )}
      </AnimatePresence>
    </div>
  );

  const errorAlert = (
    <AnimatePresence initial={false}>
      {errorMessage && (
        <motion.div
          key="err"
          initial={{ opacity: 1, y: reduce ? 0 : -8 }}
          animate={{ opacity: 1, y: 0, x: reduce ? 0 : SHAKE_X }}
          exit={{ opacity: 0 }}
          transition={{ duration: reduce ? 0.15 : 0.45 }}
        >
          <Alert variant="destructive" className="ld16-alert">
            <WarningAmberIcon sx={{ fontSize: 18, flexShrink: 0 }} />
            <span>{errorMessage}</span>
          </Alert>
        </motion.div>
      )}
    </AnimatePresence>
  );

  const viewMotion = {
    initial: { y: dist },
    animate: { y: 0 },
    exit: { opacity: 0, y: reduce ? 0 : -8, transition: { duration: 0.18 } },
    transition: { duration: reduce ? 0.15 : 0.4, ease: EASE },
  };

  return (
    <ThemeScope
      vars={THEME_VARS}
      className="login16-root ld16-page"
      style={{
        position: "relative",
        width: "100%",
        overflowX: "hidden",
        display: "flex",
        flexDirection: "column",
        background: "#ffffff",
        colorScheme: "light",
      }}
    >
      <style>{[
        "@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&display=swap');",
        ".login16-root, .login16-root * { font-family: 'IBM Plex Sans', -apple-system, 'Segoe UI', Roboto, sans-serif !important; }",
        ".ld16-page { min-height: 100vh; }",
        "@supports (min-height: 100dvh) { .ld16-page { min-height: 100dvh; } }",
        ".login16-root { -webkit-text-size-adjust: 100%; }",

        /* Layout */
        ".login16-root .ld16-main { position: relative; z-index: 1; flex: 1; width: 100%; box-sizing: border-box; display: flex; flex-direction: column; align-items: center; justify-content: center; padding: clamp(1.25rem, 4vh, 2.75rem) clamp(1rem, 4vw, 2rem) clamp(1rem, 2.5vh, 1.75rem); }",
        ".login16-root .ld16-shell { width: 100%; max-width: 460px; min-width: 0; position: relative; z-index: 1; }",
        ".login16-root .ld16-brand { display: flex; flex-direction: column; align-items: center; gap: .65rem; margin-bottom: clamp(.9rem, 2.5vh, 1.5rem); text-align: center; max-width: 100%; }",
        ".login16-root .ld16-logo { height: clamp(34px, 5vh, 46px); max-width: 100%; display: block; }",
        ".login16-root .ld16-eyebrow { gap: .5rem; padding: .28rem .8rem; font-size: .66rem; font-weight: 600; letter-spacing: 1.3px; text-transform: uppercase; color: #475569; background: #fff; border-color: #cbd5e1; max-width: 100%; text-align: center; }",
        ".login16-root .ld16-dot { width: 6px; height: 6px; border-radius: 50%; background: #14b8a6; flex-shrink: 0; }",
        "@media (max-height: 700px) { .login16-root .ld16-eyebrow { display: none; } }",
        "@media (max-width: 380px) { .login16-root .ld16-eyebrow { letter-spacing: .8px; font-size: .6rem; } }",

        /* Card */
        ".login16-root .ld16-card { position: relative; overflow: hidden; border: 1px solid #d5deea; border-radius: 14px; background: #fff; box-shadow: 0 1px 2px rgba(15,23,42,.06), 0 18px 40px -18px rgba(15,23,42,.22); }",
        ".login16-root .ld16-accent { position: absolute; top: 0; left: 0; right: 0; height: 4px; background: " + ACCENT_GRADIENT + "; transform-origin: left center; z-index: 2; pointer-events: none; }",
        ".login16-root .ld16-head { padding: clamp(1.5rem, 4vw, 2.1rem) clamp(1.1rem, 5vw, 2rem) 1.15rem; gap: .55rem; min-width: 0; }",
        ".login16-root .ld16-headtext { min-width: 0; display: flex; flex-direction: column; gap: .4rem; }",
        ".login16-root .ld16-secure { gap: .35rem; padding: .22rem .65rem; font-size: .7rem; font-weight: 600; letter-spacing: .3px; }",
        ".login16-root .ld16-title { font-size: clamp(1.4rem, 5vw, 1.7rem); font-weight: 700; letter-spacing: -.02em; line-height: 1.15; color: " + INK + "; overflow-wrap: anywhere; }",
        ".login16-root .ld16-desc { font-size: .9rem; line-height: 1.5; color: " + MUTED + "; }",
        ".login16-root .ld16-card .ui-card-content { padding: 0 clamp(1.1rem, 5vw, 2rem) 1.5rem; min-width: 0; }",
        ".login16-root .ld16-card .ui-card-footer { padding: 1rem clamp(1.1rem, 5vw, 2rem) 1.25rem; justify-content: center; text-align: center; font-size: .875rem; color: " + MUTED + "; background: #f8fafc; }",

        /* Fields */
        ".login16-root .ld16-group { min-width: 0; }",
        ".login16-root .ld16-label { color: #1e293b; font-weight: 600; font-size: .84rem; line-height: 1.2; margin-bottom: .45rem; }",
        ".login16-root .ld16-labelrow { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: .25rem .75rem; margin-bottom: .45rem; }",
        ".login16-root .ld16-labelrow .ld16-label { margin-bottom: 0; }",
        ".login16-root .ld16-field { position: relative; }",
        ".login16-root .ld16-field .ui-input { height: 3.1rem; min-height: 44px; border: 1.5px solid #7b8aa0; border-radius: 10px; background-color: #f1f5f9; color: " + INK + "; -webkit-text-fill-color: " + INK + "; caret-color: " + INK + "; opacity: 1; padding: 0 3rem 0 2.8rem; font-size: 1rem; font-weight: 500; box-shadow: none; }",
        ".login16-root .ld16-field .ld16-pw { padding-right: 5.4rem; }",
        ".login16-root .ld16-field .ui-input::placeholder { color: #64748b; -webkit-text-fill-color: #64748b; opacity: 1; }",
        ".login16-root .ld16-field .ui-input:hover { border-color: #1d4ed8; }",
        ".login16-root .ld16-field .ui-input:focus, .login16-root .ld16-field .ui-input:focus-visible { background-color: #ffffff; border-color: #1d4ed8; outline: 3px solid rgba(29,78,216,.28); outline-offset: 0; box-shadow: none; }",
        ".login16-root .ld16-field .ui-input:-webkit-autofill, .login16-root .ld16-field .ui-input:-webkit-autofill:hover, .login16-root .ld16-field .ui-input:-webkit-autofill:focus, .login16-root .ld16-field .ui-input:-webkit-autofill:active { -webkit-box-shadow: 0 0 0 100px #f1f5f9 inset !important; box-shadow: 0 0 0 100px #f1f5f9 inset !important; -webkit-text-fill-color: " + INK + " !important; caret-color: " + INK + "; border-color: #7b8aa0; }",
        ".login16-root .ld16-fieldicon { position: absolute; z-index: 1; left: .95rem; top: 50%; margin-top: -11px; color: #475569; pointer-events: none; transition: color .2s ease; }",
        ".login16-root .ld16-field:focus-within .ld16-fieldicon { color: #1d4ed8; }",
        ".login16-root .ld16-capsicon { position: absolute; z-index: 1; right: .95rem; top: 50%; margin-top: -10px; pointer-events: none; }",
        ".login16-root .ld16-capsicon-pw { right: 3.5rem; }",
        ".login16-root .ld16-toggle { position: absolute; z-index: 2; right: .2rem; top: 50%; margin-top: -22px; height: 44px; width: 44px; color: #475569; border-radius: 10px; }",
        ".login16-root .ld16-toggle:hover { color: #1d4ed8; background: rgba(29,78,216,.08); }",
        ".login16-root .ld16-helper { min-height: 1.35rem; padding-top: .3rem; }",
        ".login16-root .ld16-caps { display: inline-flex; align-items: center; gap: .35rem; color: #92400e; font-size: 12px; font-weight: 500; }",
        ".login16-root .ld16-link { display: inline-flex; align-items: center; min-height: 44px; color: #1d4ed8; text-decoration: none; font-weight: 600; font-size: .85rem; border-radius: 4px; }",
        ".login16-root .ld16-link:hover { text-decoration: underline; text-underline-offset: 3px; }",
        ".login16-root .ld16-link:focus-visible { outline: 2px solid #1d4ed8; outline-offset: 2px; }",
        ".login16-root .ld16-labelrow .ld16-link { min-height: 32px; }",
        ".login16-root .ld16-alert { display: flex; align-items: flex-start; gap: .6rem; background: #fef2f2; border-color: #fca5a5; color: #b91c1c; font-weight: 500; font-size: .85rem; margin-bottom: 1rem; border-radius: 10px; overflow-wrap: anywhere; min-width: 0; }",

        /* Buttons */
        ".login16-root .ld16-primary { width: 100%; height: 3.1rem; min-height: 44px; border-radius: 10px; font-size: .95rem; font-weight: 600; letter-spacing: .2px; color: #fff; background: linear-gradient(180deg, #2563eb 0%, #1d4ed8 100%); border: 1px solid #1e40af; box-shadow: 0 6px 14px -6px rgba(29,78,216,.55); }",
        ".login16-root .ld16-primary:hover:not(:disabled) { opacity: 1; background: linear-gradient(180deg, #1d4ed8 0%, #1e40af 100%); }",
        ".login16-root .ld16-primary:disabled { opacity: .65; box-shadow: none; }",
        ".login16-root .ld16-primary:focus-visible { outline: 3px solid rgba(29,78,216,.4); outline-offset: 2px; box-shadow: none; }",
        ".login16-root .ld16-bio { width: 100%; height: 3rem; min-height: 44px; margin-top: .75rem; border-radius: 10px; font-weight: 600; color: #1d4ed8; border-color: #93b4f5; }",
        ".login16-root .ld16-bio:hover { background: #eff6ff; border-color: #1d4ed8; }",

        /* Company cards */
        ".login16-root .ld16-companylist { display: flex; flex-direction: column; gap: .6rem; max-height: min(300px, 45vh); overflow-y: auto; overflow-x: hidden; padding: 5px; margin: 0 -5px 1.1rem; min-width: 0; }",
        ".login16-root .ld16-company { display: flex; align-items: center; gap: .85rem; min-height: 56px; min-width: 0; box-sizing: border-box; padding: .7rem .95rem; border-radius: 12px; border: 1.5px solid #b6c2d4; background: #fff; cursor: pointer; outline: none; box-shadow: none; color: " + INK + "; transition: border-color .15s ease, background-color .15s ease, box-shadow .15s ease; }",
        ".login16-root .ld16-company:hover { border-color: #1d4ed8; background: #f8fbff; }",
        ".login16-root .ld16-company:focus-visible { outline: 3px solid rgba(29,78,216,.4); outline-offset: 2px; }",
        ".login16-root .ld16-company[aria-checked='true'] { border-color: #1d4ed8; background: #eff6ff; box-shadow: 0 0 0 3px rgba(29,78,216,.14); }",
        ".login16-root .ld16-avatar { width: 38px; height: 38px; border-radius: 10px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: .95rem; color: #1e3a8a; background: #dbeafe; }",
        ".login16-root .ld16-company[aria-checked='true'] .ld16-avatar { color: #fff; background: " + ACCENT_GRADIENT + "; }",
        ".login16-root .ld16-companyname { flex: 1; min-width: 0; font-weight: 600; font-size: .92rem; line-height: 1.3; color: " + INK + "; overflow-wrap: anywhere; word-break: break-word; }",
        ".login16-root .ld16-radio { width: 22px; height: 22px; border-radius: 50%; flex-shrink: 0; display: flex; align-items: center; justify-content: center; border: 2px solid #94a3b8; background: #fff; }",
        ".login16-root .ld16-company[aria-checked='true'] .ld16-radio { border-color: #1d4ed8; background: #1d4ed8; color: #fff; }",

        /* Trust row */
        ".login16-root .ld16-trust { list-style: none; margin: clamp(1rem, 3vh, 1.5rem) 0 0; padding: 0; display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: .5rem; width: 100%; }",
        ".login16-root .ld16-trustitem { display: flex; flex-direction: column; align-items: center; text-align: center; gap: .4rem; padding: .2rem .15rem; min-width: 0; }",
        ".login16-root .ld16-trusticon { width: 36px; height: 36px; border-radius: 10px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; background: #fff; border: 1px solid #d5deea; box-shadow: 0 1px 2px rgba(15,23,42,.05); }",
        ".login16-root .ld16-trusttext { display: flex; flex-direction: column; line-height: 1.25; min-width: 0; }",
        ".login16-root .ld16-trusttext strong { font-size: .8rem; font-weight: 700; color: #0f172a; }",
        ".login16-root .ld16-trusttext small { font-size: .7rem; color: #475569; font-weight: 500; }",
        "@media (max-width: 479.95px) { .login16-root .ld16-trust { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: .6rem; } .login16-root .ld16-trustitem { flex-direction: row; text-align: left; gap: .6rem; padding: .45rem .6rem; border: 1px solid #e2e8f0; border-radius: 10px; background: #fff; } .login16-root .ld16-trusttext { align-items: flex-start; } }",
        "@media (max-height: 420px) and (orientation: landscape) { .login16-root .ld16-trust { grid-template-columns: repeat(4, minmax(0, 1fr)); } .login16-root .ld16-trustitem { flex-direction: row; text-align: left; justify-content: center; } .login16-root .ld16-trusttext { align-items: flex-start; } }",

        ".login16-root .ld16-copy { margin: clamp(.9rem, 2.5vh, 1.4rem) 0 0; font-size: .75rem; color: #475569; text-align: center; overflow-wrap: anywhere; }",

        /* Session dialog */
        ".login16-root .ld16-signout { min-width: 88px; min-height: 44px; flex-shrink: 0; font-size: .78rem; font-weight: 700; border-radius: 10px; }",
        ".login16-root .ld16-cancel { width: 100%; min-height: 44px; font-weight: 600; color: #334155; border-radius: 10px; border-color: #94a3b8; }",
        ".login16-root .ld16-cancel:hover { background: #f1f5f9; border-color: #1d4ed8; color: #1d4ed8; }",

        "@media (prefers-reduced-motion: reduce) { .login16-root, .login16-root *, .login16-root *::before, .login16-root *::after { animation-duration: .01ms !important; animation-iteration-count: 1 !important; transition-duration: .01ms !important; } }",
      ].join("\n")}</style>

      {/* ── DECORATIVE BACKGROUND (static, behind everything) ── */}
      <div aria-hidden="true" style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none", zIndex: 0 }}>
        <div style={{ position: "absolute", inset: 0, background: GLOWS }} />
        <div style={{ position: "absolute", inset: 0, backgroundImage: ISO_TILE, backgroundSize: "52px 30px", WebkitMaskImage: PATTERN_MASK, maskImage: PATTERN_MASK }} />
      </div>

      {/* Thin page top accent bar */}
      <motion.div
        aria-hidden="true"
        initial={{ scaleX: reduce ? 1 : 0 }}
        animate={{ scaleX: 1 }}
        transition={{ duration: reduce ? 0.1 : 0.9, ease: EASE }}
        style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: ACCENT_GRADIENT, transformOrigin: "left center", pointerEvents: "none", zIndex: 2 }}
      />

      {/* ── CONTENT ── */}
      <div className="ld16-main">
        {/* Brand */}
        <motion.div className="ld16-brand" {...enter(0)}>
          <img className="ld16-logo" src="/RBLOGOBLUE.svg" alt="ReonBook" />
          <Badge variant="outline" className="ld16-eyebrow">
            <span className="ld16-dot" aria-hidden="true" />
            Enterprise Accounting Platform
          </Badge>
        </motion.div>

        <div className="ld16-shell">
          <MotionCard
            className="ld16-card"
            initial={{ opacity: 1, y: reduce ? 0 : 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: reduce ? 0.2 : 0.7, delay: reduce ? 0 : 0.08, ease: EASE }}
          >
            <motion.div
              className="ld16-accent"
              aria-hidden="true"
              initial={{ scaleX: reduce ? 1 : 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: reduce ? 0.1 : 0.8, delay: reduce ? 0 : 0.35, ease: EASE }}
            />
            <AnimatePresence mode="wait" initial>
              {!isCompanySelectionVisible ? (

                /* ── LOGIN FORM ── */
                <motion.div key="login" {...viewMotion}>
                  <CardHeader className="ld16-head">
                    <motion.div {...enter(0.2)}>
                      <Badge variant="secondary" className="ld16-secure">
                        <LockOutlinedIcon sx={{ fontSize: 13 }} aria-hidden="true" />
                        Secure sign-in
                      </Badge>
                    </motion.div>
                    <motion.div {...enter(0.26)} className="ld16-headtext">
                      <CardTitle role="heading" aria-level={1} className="ld16-title">Welcome</CardTitle>
                      <CardDescription className="ld16-desc">Sign in to access your ReonBook dashboard</CardDescription>
                    </motion.div>
                  </CardHeader>

                  <CardContent>
                    <form onSubmit={(e) => { e.preventDefault(); setIsAutoLogin(false); FN_HandleLogin(); }}>

                      <motion.div className="ld16-group" {...rise(0.32)}>
                        <Label htmlFor="ld16-identifier" className="ld16-label">
                          Username, Email or Mobile
                        </Label>
                        <div className="ld16-field">
                          <PersonOutlineIcon className="ld16-fieldicon" sx={{ fontSize: 22 }} aria-hidden="true" />
                          <Input
                            id="ld16-identifier"
                            type="text"
                            autoComplete="username"
                            placeholder="Username, email or mobile number"
                            value={identifier}
                            onChange={(e) => setIdentifier(e.target.value)}
                            onKeyUp={handleCapsLockCheck} onFocus={handleCapsLockCheck} onBlur={() => setCapsLockOn(false)}
                          />
                          {capsLockOn && <WarningAmberIcon className="ld16-capsicon" sx={{ color: "#b45309", fontSize: 20 }} aria-hidden="true" />}
                        </div>
                        {capsHelper}
                      </motion.div>

                      <motion.div className="ld16-group" style={{ marginTop: ".6rem" }} {...rise(0.38)}>
                        <div className="ld16-labelrow">
                          <Label htmlFor="ld16-password" className="ld16-label">Password</Label>
                          <Link to="/forgot-password" className="ld16-link">Forgot password?</Link>
                        </div>
                        <div className="ld16-field">
                          <LockOutlinedIcon className="ld16-fieldicon" sx={{ fontSize: 22 }} aria-hidden="true" />
                          <Input
                            id="ld16-password"
                            className="ld16-pw"
                            type={showPassword ? "text" : "password"}
                            autoComplete="current-password"
                            placeholder="Enter your password"
                            value={userPassword}
                            onChange={(e) => setUserPassword(e.target.value)}
                            onKeyUp={handleCapsLockCheck} onFocus={handleCapsLockCheck} onBlur={() => setCapsLockOn(false)}
                          />
                          {capsLockOn && <WarningAmberIcon className="ld16-capsicon ld16-capsicon-pw" sx={{ color: "#b45309", fontSize: 20 }} aria-hidden="true" />}
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="ld16-toggle"
                            onClick={handleTogglePasswordVisibility}
                            aria-label={showPassword ? "Hide password" : "Show password"}
                            aria-pressed={showPassword}
                          >
                            {showPassword ? <VisibilityOff sx={{ fontSize: 20 }} /> : <Visibility sx={{ fontSize: 20 }} />}
                          </Button>
                        </div>
                        {capsHelper}
                      </motion.div>

                      <motion.div style={{ marginTop: ".7rem" }} {...rise(0.44)}>
                        {errorAlert}

                        <MotionButton
                          type="submit"
                          size="lg"
                          disabled={isLoading}
                          aria-busy={isLoading}
                          className="ld16-primary"
                          whileHover={reduce || isLoading ? undefined : { y: -1 }}
                          whileTap={reduce || isLoading ? undefined : { scale: 0.985 }}
                          transition={{ type: "spring", stiffness: 400, damping: 24 }}
                        >
                          {isLoading
                            ? <><CircularProgress size={18} thickness={5} sx={{ color: "#fff" }} />Signing in...</>
                            : <>Sign In<ArrowForwardIcon sx={{ fontSize: 20 }} /></>}
                        </MotionButton>

                        {typeof window !== "undefined" && window.isNativeApp && window.ReactNativeWebView && (
                          <Button
                            type="button"
                            variant="outline"
                            className="ld16-bio"
                            onClick={() => window.ReactNativeWebView?.postMessage(JSON.stringify({ type: "REQUEST_BIOMETRIC" }))}>
                            Login with Biometric
                          </Button>
                        )}
                      </motion.div>
                    </form>
                  </CardContent>

                  <motion.div {...enter(0.5)}>
                    <Separator />
                  </motion.div>
                  <CardFooter>
                    <motion.p {...enter(0.54)} style={{ margin: 0 }}>
                      Don't have an account?{" "}
                      <Link to="/register" className="ld16-link" style={{ fontSize: "inherit", minHeight: 0 }}>
                        Sign up now
                      </Link>
                    </motion.p>
                  </CardFooter>
                </motion.div>

              ) : (

                /* ── COMPANY SELECTION ── */
                <motion.div key="company" {...viewMotion}>
                  <CardHeader className="ld16-head">
                    <motion.div {...enter(0.15)}>
                      <Badge variant="secondary" className="ld16-secure">
                        <BusinessIcon sx={{ fontSize: 13 }} aria-hidden="true" />
                        {companies.length} organization{companies.length !== 1 ? "s" : ""}
                      </Badge>
                    </motion.div>
                    <motion.div {...enter(0.2)} className="ld16-headtext">
                      <CardTitle role="heading" aria-level={1} className="ld16-title">Select Organization</CardTitle>
                      <CardDescription className="ld16-desc" id="ld16-company-desc">Choose which organization to access</CardDescription>
                    </motion.div>
                  </CardHeader>

                  <CardContent>
                    <motion.div
                      {...rise(0.26)}
                      className="ld16-companylist"
                      role="radiogroup"
                      aria-label="Choose your organization"
                      aria-describedby="ld16-company-desc"
                    >
                      {companies.map((c, idx) => {
                        const isSel = selectedCompany?.companyId === c.companyId;
                        const firstFocusable = !selectedCompany && idx === 0;
                        return (
                          <MotionCard
                            key={c.companyId}
                            id={"ld16-company-" + idx}
                            role="radio"
                            aria-checked={isSel}
                            tabIndex={isSel || firstFocusable ? 0 : -1}
                            className="ld16-company"
                            onClick={() => setSelectedCompany(companies.find((x) => x.companyId === c.companyId))}
                            onKeyDown={(e) => handleCompanyKeyDown(e, idx)}
                            whileHover={reduce ? undefined : { y: -2 }}
                            whileTap={reduce ? undefined : { scale: 0.99 }}
                            transition={{ type: "spring", stiffness: 380, damping: 24 }}
                          >
                            <span className="ld16-avatar" aria-hidden="true">{(c.companyName || "?").trim().charAt(0).toUpperCase()}</span>
                            <span className="ld16-companyname">{c.companyName}</span>
                            <span className="ld16-radio" aria-hidden="true">
                              <AnimatePresence initial={false}>
                                {isSel && (
                                  <motion.span
                                    key="tick"
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    exit={{ scale: 0 }}
                                    transition={{ duration: reduce ? 0.05 : 0.18 }}
                                    style={{ display: "flex" }}
                                  >
                                    <CheckIcon sx={{ fontSize: 14 }} />
                                  </motion.span>
                                )}
                              </AnimatePresence>
                            </span>
                          </MotionCard>
                        );
                      })}
                    </motion.div>

                    <motion.div {...rise(0.32)}>
                      {errorAlert}

                      <MotionButton
                        type="button"
                        size="lg"
                        className="ld16-primary"
                        onClick={() => FN_SelectCompany()}
                        whileHover={reduce ? undefined : { y: -1 }}
                        whileTap={reduce ? undefined : { scale: 0.985 }}
                        transition={{ type: "spring", stiffness: 400, damping: 24 }}
                      >
                        Continue to Dashboard<ArrowForwardIcon sx={{ fontSize: 20 }} />
                      </MotionButton>
                    </motion.div>
                  </CardContent>
                </motion.div>
              )}
            </AnimatePresence>
          </MotionCard>

          {/* Trust row */}
          <ul className="ld16-trust" aria-label="Security and compliance">
            {trust.map((t, i) => (
              <motion.li
                key={t.top}
                className="ld16-trustitem"
                {...enter(0.7 + i * 0.07)}
                whileHover={reduce ? undefined : { y: -2 }}
              >
                <span className="ld16-trusticon" aria-hidden="true">
                  <t.Icon sx={{ fontSize: 19, color: t.color }} />
                </span>
                <span className="ld16-trusttext">
                  <strong>{t.top}</strong>
                  <small>{t.sub}</small>
                </span>
              </motion.li>
            ))}
          </ul>
        </div>

        <motion.p className="ld16-copy" {...enter(1)}>
          <CopyrightIcon sx={{ fontSize: 12, verticalAlign: "middle", mr: 0.4 }} />
          {new Date().getFullYear()} Powered by{" "}
          Soltrack Technologies Pvt Ltd
        </motion.p>
      </div>

      {/* ── SESSION LIMIT PICKER MODAL ── */}
      <Dialog
        className="login16-root"
        open={!!sessionPickerData}
        onClose={() => setSessionPickerData(null)}
        maxWidth="xs"
        fullWidth
        scroll="body"
        aria-labelledby="ld16-session-title"
        slots={{ transition: Grow }}
        transitionDuration={{ enter: 320, exit: 180 }}
        slotProps={{
          backdrop: { sx: { bgcolor: "rgba(15,23,42,0.5)" } },
          paper: { sx: { borderRadius: "14px", overflow: "hidden", m: 2, width: "calc(100% - 32px)", maxWidth: 440 } },
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          <ThemeScope vars={THEME_VARS} className="login16-root">
            <Box sx={{ height: 4, background: ACCENT_GRADIENT }} />
            <Box sx={{ px: { xs: 2, sm: 3 }, pt: 2.5, pb: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5, minWidth: 0 }}>
                <Box sx={{ width: 42, height: 42, borderRadius: "12px", bgcolor: "#eff6ff", border: "1px solid #bfdbfe", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <DevicesIcon sx={{ color: "#1d4ed8", fontSize: 22 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography id="ld16-session-title" component="h2" sx={{ fontWeight: 700, fontSize: "1.08rem", color: INK, lineHeight: 1.3 }}>
                    Session Limit Reached
                  </Typography>
                  <Typography sx={{ fontSize: "0.8rem", color: MUTED, mt: 0.2 }}>
                    {sessionPickerData?.maxSessions} of {sessionPickerData?.maxSessions} allowed session{sessionPickerData?.maxSessions !== 1 ? "s" : ""} in use
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ bgcolor: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: "10px", px: 2, py: 1 }}>
                <Typography sx={{ fontSize: "0.82rem", color: "#1e3a8a", lineHeight: 1.5 }}>
                  Sign out of one of the devices below to log in on this device.
                </Typography>
              </Box>
            </Box>

            <Box sx={{ px: { xs: 2, sm: 3 }, pt: 0.5, pb: 1 }}>
              <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: MUTED, letterSpacing: "0.8px", textTransform: "uppercase", mb: 1.5 }}>
                Active Sessions
              </Typography>

              {sessionPickerData?.activeSessions?.map((session, idx) => (
                <Box key={session.id}
                  component={motion.div}
                  initial={{ y: reduce ? 0 : 14 }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.4, delay: reduce ? 0 : 0.08 + idx * 0.06, ease: EASE }}
                  sx={{
                    display: "flex", alignItems: "center", gap: 1.5, minWidth: 0,
                    p: 1.5, mb: 1, borderRadius: "10px",
                    border: "1px solid #cbd5e1",
                    bgcolor: "#ffffff",
                    transition: "border-color .2s ease",
                    "&:hover": { borderColor: "#1d4ed8" },
                  }}>
                  <Box sx={{ width: 38, height: 38, borderRadius: "10px", bgcolor: "#dbeafe", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    {deviceIcon(session.device_type)}
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography noWrap sx={{ fontWeight: 600, fontSize: "0.87rem", color: INK }}>
                      {session.device_name || "Unknown Device"}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.3, flexWrap: "wrap" }}>
                      <AccessTimeIcon sx={{ fontSize: 12, color: MUTED }} />
                      <Typography sx={{ fontSize: "0.75rem", color: MUTED }}>
                        {timeAgo(session.last_activity)}
                      </Typography>
                      {session.ip_address && (
                        <Typography sx={{ fontSize: "0.75rem", color: MUTED, ml: 0.5, overflowWrap: "anywhere" }}>
                          · {session.ip_address}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  <Button
                    type="button"
                    size="sm"
                    variant="destructive"
                    className="ld16-signout"
                    disabled={kickingSessionId === session.id}
                    onClick={async () => {
                      setKickingSessionId(session.id);
                      setSessionPickerData(null);
                      await FN_HandleLogin(identifier, userPassword, session.id);
                      setKickingSessionId(null);
                    }}
                  >
                    {kickingSessionId === session.id
                      ? <CircularProgress size={13} sx={{ color: "#fff" }} />
                      : "Sign Out"}
                  </Button>
                </Box>
              ))}
            </Box>

            <Box sx={{ px: { xs: 2, sm: 3 }, pb: 2.5, pt: 0.5, textAlign: "center" }}>
              <Button
                type="button"
                variant="outline"
                className="ld16-cancel"
                onClick={() => setSessionPickerData(null)}
              >
                Cancel
              </Button>
            </Box>
          </ThemeScope>
        </DialogContent>
      </Dialog>

    </ThemeScope>
  );
}

export default LoginDesign16;
